<template>
  <ValidationObserver ref="wizardPage5" id="wizardPage5" name="wizardPage5">
    <div v-if="fleetIncomplete" class="mt-5 mb-5 text-danger">
      <strong>WARNING - Some fleet information is incomplete. Please ensure this is resolved before binding cover.</strong>
    </div>

    <!-- begin: Client Business Details -->
    <ValidationObserver ref="clientBusinessDetails">
      <h3 class="font-weight-bold text-dark">
        <b-button class="btn btn-icon btn-sm ml-1" v-b-toggle="'clientBusinessDetailsPanel'">
          <img v-if="!isPanel1Expanded" src="media/svg/icons/Navigation/Angle-double-down.svg" title="Expand"/>
          <img v-if="isPanel1Expanded" src="media/svg/icons/Navigation/Angle-double-up.svg" title="Collapse"/>
        </b-button>
        Client Business Details
      </h3>
      <b-collapse id="clientBusinessDetailsPanel" class="mb-5" v-model="isPanel1Expanded">
        <b-card border-variant="primary" header-bg-variant="primary" header-text-variant="white" header-class="p-3 font-size-lg font-weight-bold"
          v-if="appData && appData.client && appData.policy && appData.transaction"
        >
          Provided below are details of the client's business as specified in their Motor Proposal Form submission.
          These values can be updated below. All fields below are mandatory.<br />

          <div class="row mt-5 mb-0">
            <div class="col-6">
              <!-- Rentsure/CRI Business Activities -->
              <div v-if="!isBrandFuse">
                <table class="table table-sm table-bordered">
                  <thead>
                    <th width="60%" class="text-center">Business Activity</th>
                    <th width="40%" class="text-center">Percentage (%)</th>
                  </thead>
                  <tr v-for="(businessActivity, index) in appData.transaction.K_BusinessActivitiesJson" :key="index">
                    <td>
                      <ValidationProvider rules="required" :name="`businessActivity${index}.key`" v-slot="{ errors }">
                        <b-form-select class="custom-select custom-select-md" v-model="businessActivity.key" :disabled="isReadOnly">
                          <option v-for="ba in appData.allBusinessActivities" :value="ba.K_BusinessActivityKey" :key="ba.K_BusinessActivityKey">
                            {{ ba.Name }}
                          </option>
                        </b-form-select>
                        <span class="text-danger">{{ errors[0] }}</span>
                      </ValidationProvider>
                    </td>
                    <td>
                      <ValidationProvider rules="required|min_value:1|max_value:100" :name="`businessActivity${index}.percentage`" v-slot="{ errors }">
                        <input type="number" min="1" max="100" class="form-control" v-model="businessActivity.percentage"
                               @input="calculateBusinessActivityPercentTotal" :disabled="isReadOnly"/>
                        <span class="text-danger">{{ errors[0] }}</span>
                      </ValidationProvider>
                    </td>
                    <td>
                      <button class="btn btn-sm btn-outline-danger" @click.prevent="deleteBusinessActivity(index)" title="Delete" v-if="!isReadOnly">
                        <i class="fa fa-trash-alt"></i>
                      </button>
                    </td>
                  </tr>
                </table>
                <ValidationProvider :rules="{ 'required': { allowFalse: false }}" name="atleast1BusinessActivitySelected" v-slot="{ errors }">
                  <input type="number" hidden="hidden" class="form-control" v-model="atleast1BusinessActivitySelected"
                         :disabled="isReadOnly"/>
                  <span class="text-danger">{{ errors[0] }}</span>
                </ValidationProvider>

                <div class="form-group row mb-0">
                <span class="col-9 text-right">
                  <label class="col-form-label"><strong> Total (%) </strong></label>
                </span>
                  <span class="col-3 pt-3">
                  <strong>{{ appData.transaction.businessActivitiesPercentTotal }}</strong>
                  <ValidationProvider rules="required|totalPercentage" name="businessActivitiesPercentTotal" v-slot="{ errors }">
                    <input type="number" v-model="appData.transaction.businessActivitiesPercentTotal" hidden/>
                    <span class="text-danger"><br>{{ errors[0] }}</span>
                  </ValidationProvider>
                </span>
                </div>
                <div>
                  <button class="btn btn-primary" @click.prevent="addBusinessActivity()" v-if="!isReadOnly">
                    Add Business Activity
                  </button>
                </div>
              </div>
              <!-- End of Rentsure/CRI Business Activities -->

              <!-- Fuse Business Activities -->
              <div v-if="isBrandFuse" class="form-group">
                <label class="col-form-label"><strong>Business Activities</strong></label>
                <ValidationProvider rules="required|max:2000" name="baManual" v-slot="{ errors }">
                  <textarea type="text" maxlength="2000" class="form-control" v-model="appData.transaction.K_FuseBusinessActivities"
                         rows="4" :disabled="isReadOnly"/>
                  <span class="text-danger">{{ errors[0] }}</span>
                </ValidationProvider>
              </div>
              <!-- End of Fuse Business Activities -->
            </div>

            <div class="col-6">
              <table class="table table-sm table-bordered">
                <thead>
                  <th width="60%" class="text-center">Other Details</th>
                  <th width="40%" class="text-center"></th>
                </thead>
                <tr>
                  <td class="align-middle">% Finance</td>
                  <td>
                    <ValidationProvider rules="required" name="K_PercentageOfFleetFinanced" v-slot="{ errors }">
                      <b-form-select class="form-control" v-model="appData.transaction.K_PercentageOfFleetFinanced" :disabled="isReadOnly">
                        <option></option>
                        <option value="None">None</option>
                        <option value="1-10%">1-10%</option>
                        <option value="11-20%">11-20%</option>
                        <option value="21-30%">21-30%</option>
                        <option value="31-40%">31-40%</option>
                        <option value="41-50%">41-50%</option>
                        <option value="51% or Over">51% or Over</option>
                      </b-form-select>
                      <span class="text-danger"><br>{{ errors[0] }}</span>
                    </ValidationProvider>
                  </td>
                </tr>
                <tr v-if="!isBrandFuse">
                  <td class="align-middle">% Cash Rental</td>
                  <td v-if="appData.transaction.K_TransactionVersion"> <!-- New Transaction -->
                    <ValidationProvider rules="required" name="K_PercentageOfCashRentals" v-slot="{ errors }">
                      <b-form-select class="form-control" v-model="appData.transaction.K_PercentageOfCashRentals" :disabled="isReadOnly">
                        <option></option>
                        <option value="None">None</option>
                        <option value="1-5%">1-5%</option>
                        <option value="6-10%">6-10%</option>
                        <option value="11-15%">11-15%</option>
                        <option value="16-20%">16-20%</option>
                        <option value="21% or Over">21% or Over</option>
                      </b-form-select>
                      <span class="text-danger"><br>{{ errors[0] }}</span>
                    </ValidationProvider>
                  </td>
                  <td v-else>
                    <ValidationProvider rules="required" name="K_PercentageOfCashRentals" v-slot="{ errors }">
                      <b-form-select class="form-control" v-model="appData.client.K_PercentageOfCashRentals" :disabled="isReadOnly">
                        <option></option>
                        <option value="None">None</option>
                        <option value="1-5%">1-5%</option>
                        <option value="6-10%">6-10%</option>
                        <option value="11-15%">11-15%</option>
                        <option value="16-20%">16-20%</option>
                        <option value="21% or Over">21% or Over</option>
                      </b-form-select>
                      <span class="text-danger"><br>{{ errors[0] }}</span>
                    </ValidationProvider>
                  </td>
                </tr>
                <tr>
                  <td class="align-middle">NSW Stamp Duty Exempt</td>
                  <td v-if="appData.transaction.K_TransactionVersion"> <!-- New transactions -->
                    <ValidationProvider rules="required" name="K_NswStampDutyExempt" v-slot="{ errors }">
                      <b-form-radio-group v-model="appData.transaction.K_NswStampDutyExempt" class="form-control" :disabled="isReadOnly">
                        <b-form-radio value="Yes">Yes</b-form-radio>
                        <b-form-radio class="ml-2" value="No">No</b-form-radio>
                      </b-form-radio-group>
                    <span class="text-danger"><br>{{ errors[0] }}</span>
                    </ValidationProvider>
                  </td>
                  <td v-else>
                    <ValidationProvider rules="required" name="K_NswStampDutyExempt" v-slot="{ errors }">
                      <b-form-radio-group v-model="appData.client.K_NswStampDutyExempt" class="form-control" :disabled="isReadOnly">
                        <b-form-radio value="Yes">Yes</b-form-radio>
                        <b-form-radio class="ml-2" value="No">No</b-form-radio>
                      </b-form-radio-group>
                      <span class="text-danger"><br>{{ errors[0] }}</span>
                    </ValidationProvider>
                  </td>
                </tr>
              </table>
            </div>

            <!-- Fuse Vehicle Use % -->
            <div v-if="isBrandFuse" class="form-group row col-12 mb-0">
              <table class="table table-sm table-bordered">
                <thead>
                <th width="80%" class="text-center">Vehicle Use</th>
                <th width="20%" class="text-center">Fleet Mix (%)</th>
                </thead>
                <tr>
                  <td class="text-center" style="vertical-align: middle">Low - Private use, typically makes 2 or less trips a day, less than 10,000 annual km travelled.</td>
                  <td class="text-center">
                    <ValidationProvider rules="required|min_value:0|max_value:100" name="vehicleUseLow" v-slot="{ errors }">
                      <input type="number" min="0" max="100" class="form-control" v-model="appData.transaction.K_VehicleUseJson.lowPercent" :disabled="isReadOnly"/>
                      <span class="text-danger">{{ errors[0] }}</span>
                    </ValidationProvider>
                  </td>
                </tr>
                <tr>
                  <td class="text-center" style="vertical-align: middle">Medium - On the road but less than half the day, 3-5 trips a day, 10,000 to 20,000 annual km travelled.</td>
                  <td class="text-center">
                    <ValidationProvider rules="required|min_value:0|max_value:100" name="vehicleUseMedium" v-slot="{ errors }">
                      <input type="number" min="0" max="100" class="form-control" v-model="appData.transaction.K_VehicleUseJson.mediumPercent" :disabled="isReadOnly"/>
                      <span class="text-danger">{{ errors[0] }}</span>
                    </ValidationProvider>
                  </td>
                </tr>
                <tr>
                  <td class="text-center" style="vertical-align: middle">High - On the road for most of the day, 5-10 trips a day, 20,000 to 30,000 annual km travelled.</td>
                  <td class="text-center">
                    <ValidationProvider rules="required|min_value:0|max_value:100" name="vehicleUseHigh" v-slot="{ errors }">
                      <input type="number" min="0" max="100" class="form-control" v-model="appData.transaction.K_VehicleUseJson.highPercent" :disabled="isReadOnly"/>
                      <span class="text-danger">{{ errors[0] }}</span>
                    </ValidationProvider>
                  </td>
                </tr>
                <tr>
                  <td class="text-center" style="vertical-align: middle">Extreme - On the road for the whole day, 10+ trips a day, more than 30,000 annual km travelled.</td>
                  <td class="text-center">
                    <ValidationProvider rules="required|min_value:0|max_value:100" name="vehicleUseExtreme" v-slot="{ errors }">
                      <input type="number" min="0" max="100" class="form-control" v-model="appData.transaction.K_VehicleUseJson.extremePercent" :disabled="isReadOnly"/>
                      <span class="text-danger">{{ errors[0] }}</span>
                    </ValidationProvider>
                  </td>
                </tr>
                <tr>
                  <td class="text-right" style="vertical-align: middle">Total</td>
                  <td class="text-center">
                    <label class="col-form-label ml-2"><strong>{{ totalVehicleUsePercent }} %</strong></label>
                    <ValidationProvider rules="required|totalPercentage" name="totalVehicleUsePercent" v-slot="{ errors }">
                      <input type="number" v-model="totalVehicleUsePercent" hidden/>
                      <span class="text-danger"><br>{{ errors[0] }}</span>
                    </ValidationProvider>
                  </td>
                </tr>
              </table>
            </div>
            <!-- End of Fuse Vehicle Use % -->
          </div>
        </b-card>
      </b-collapse>
    </ValidationObserver>
    <!-- end: Client Business Details -->

    <!-- begin: Current Policy Details -->
    <ValidationObserver ref="currentPolicyDetails">
      <h3 class="font-weight-bold text-dark mt-10" v-if="!isRenewalTransaction">
        <b-button class="btn btn-icon btn-sm ml-1" v-b-toggle="'currentPolicyDetailsPanel'">
          <img v-if="!isPanel2Expanded" src="media/svg/icons/Navigation/Angle-double-down.svg" title="Expand" alt="Expand"/>
          <img v-if="isPanel2Expanded" src="media/svg/icons/Navigation/Angle-double-up.svg" title="Co
          llapse" alt="Collapse"/>
        </b-button>
        Current Policy Details
      </h3>
      <b-collapse id="currentPolicyDetailsPanel" class="mb-5" v-model="isPanel2Expanded">
        <b-card border-variant="primary" header-bg-variant="primary" header-text-variant="white" header-class="p-3 font-size-lg font-weight-bold"
                v-if="appData && appData.transaction"
        >
          <strong>Provided below are details of the client's current policy as specified in their Motor Proposal Form submission.</strong>
          <div class="form-group row mb-0 mt-2">
            <label class="col-3 col-form-label text-right">Current Insurer</label>
            <div class="col-3 font-weight-bold col-form-label">
              {{ appData.transaction.K_CurrentInsurerName }}
            </div>

            <label class="col-3 col-form-label text-right">Current Policy Number</label>
            <div class="col-3 font-weight-bold col-form-label">
              {{ appData.transaction.K_CurrentPolicyNumber }}
            </div>
          </div>

          <div class="form-group row mb-0">
            <label class="col-3 col-form-label text-right">Current Broker Name</label>
            <div class="col-3 font-weight-bold col-form-label">
              {{ appData.transaction.K_CurrentBroker }}
            </div>

            <label class="col-3 col-form-label text-right">Current Expiry Date</label>
            <div class="col-3 font-weight-bold col-form-label" v-if="appData.transaction.K_CurrentExpiryDate">
              {{ appData.transaction.K_CurrentExpiryDate | dateParse("YYYY-MM-DD") | dateFormat("DD/MM/YYYY") }}
            </div>
          </div>

          <div class="form-group row mb-0">
            <label class="col-3 col-form-label text-right">Special Excesses/Conditions</label>
            <div class="col-9 font-weight-bold col-form-label">
              {{ appData.transaction.K_SpecialExcessesConditions }}
            </div>
          </div>
        </b-card>
      </b-collapse>
    </ValidationObserver>
    <!-- end: Current Policy Details -->

    <!-- begin: Requirements of New Policy -->
    <ValidationObserver ref="requirementsOfPolicy">
      <h3 class="font-weight-bold text-dark mt-10">
        <b-button class="btn btn-icon btn-sm ml-1" v-b-toggle="'panel3'">
          <img v-if="!isPanel3Expanded" src="media/svg/icons/Navigation/Angle-double-down.svg" title="Expand" alt="Expand"/>
          <img v-if="isPanel3Expanded" src="media/svg/icons/Navigation/Angle-double-up.svg" title="Collapse" alt="Collapse"/>
        </b-button>
        Requirements of New Policy
      </h3>
      <b-collapse v-model="isPanel3Expanded" id="panel3" class="mb-5">
        <b-card border-variant="primary" header-bg-variant="primary" header-text-variant="white" header-class="p-3 font-size-lg font-weight-bold"
          v-if="appData && appData.transaction"
        >
          <strong>
            Provided below are details of the client's business as specified in their Motor Proposal Form submission.
            These values can be overridden by updating the values below.All fields below are mandatory.
          </strong><br/>

          <div class="form-group row mb-0 mt-5">
            <label class="col-3 col-form-label text-right">Policy Start Date</label>
            <div class="col-3">
              <ValidationProvider rules="required" name="K_ProposedStartDate" v-slot="{ errors }">
                <b-form-datepicker
                  class="form-control" v-model="appData.transaction.K_ProposedStartDate"
                  :date-format-options="{ year: 'numeric', month: '2-digit', day: '2-digit' }" locale="en-AU"
                  :disabled="isReadOnly"
                >
                </b-form-datepicker>
                <span class="text-danger"><br>{{ errors[0] }}</span>
              </ValidationProvider>
            </div>

            <label class="col-3 col-form-label text-right">Required Excess</label>
            <div class="col-3">
              <ValidationProvider rules="required" name="K_RequiredExcess" v-slot="{ errors }">
                <b-form-select class="form-control" v-model="appData.transaction.K_RequiredExcess"
                               :options="allowedExcesses" text-field="name" value-field="value" @input="excessSelected"
                               :disabled="isReadOnly"
                >
                </b-form-select>
                <span class="text-danger">{{ errors[0] }}</span>
              </ValidationProvider>
            </div>
          </div>

          <div class="form-group row mb-0">
            <label class="col-3 col-form-label text-right">Policy End Date</label>
            <div class="col-3">
              <ValidationProvider rules="required" name="K_ProposedEndDate" v-slot="{ errors }">
                <b-form-datepicker
                  class="form-control" v-model="appData.transaction.K_ProposedEndDate"
                  :date-format-options="{ year: 'numeric', month: '2-digit', day: '2-digit'}" locale="en-AU"
                  :disabled="isReadOnly"
                ></b-form-datepicker>
                <span class="text-danger"><br>{{ errors[0] }}</span>
              </ValidationProvider>
            </div>

            <label class="col-3 col-form-label text-right">Alternate Excess 1</label>
            <div class="col-3">
              <b-form-select size="sm" class="form-control" v-model="appData.transaction.K_AlternateExcess1"
                             :options="allowedExcesses" text-field="name" value-field="value" @input="excessSelected"
                             :disabled="isReadOnly || !alternateExcess1Enabled"
              >
              </b-form-select>
            </div>
          </div>

          <div class="form-group row mb-0">
            <label class="col-3 col-form-label text-right">Premium Type</label>
            <div class="col-3 mb-1">
              <ValidationProvider rules="required" name="K_PaymentMethod" v-slot="{ errors }">
                <b-form-select class="form-control" v-model="appData.transaction.K_PaymentMethod"
                               @input="paymentMethodChanged" :disabled="isReadOnly">
                  <option></option>
                  <option value="Monthly Premium">Monthly Premium</option>
                  <option value="Annual Premium">Annual Premium</option>
                  <option value="Burning Cost">Burning Cost</option>
                </b-form-select>
                <span class="text-danger"><br>{{ errors[0] }}</span>
              </ValidationProvider>
            </div>
            <label class="col-3 col-form-label text-right">Alternate Excess 2</label>
            <div class="col-3">
              <b-form-select size="sm" class="form-control mt-1" v-model="appData.transaction.K_AlternateExcess2"
                             :options="allowedExcesses" text-field="name" value-field="value"  @input="excessSelected"
                             :disabled="isReadOnly || !alternateExcess2Enabled"
              >
              </b-form-select>
              <ValidationProvider :rules="{ 'required': { allowFalse: false }}" name="excessesValid" v-slot="{ errors }">
                <input type="checkbox" hidden="hidden" class="form-control" v-model="excessesValid"/>
                <span class="text-danger">{{ errors[0] ? 'All excess values must be different' : '' }}</span>
              </ValidationProvider>
            </div>
          </div>

        </b-card>
      </b-collapse>
    </ValidationObserver>
    <!-- end: Requirements of New Policy -->

    <!-- begin: Fleet History -->
    <ValidationObserver ref="fleetHistory">
      <h3 class="font-weight-bold text-dark mt-10">
        <b-button class="btn btn-icon btn-sm ml-1" v-b-toggle="'panel4'">
          <img v-if="!isPanel4Expanded" src="media/svg/icons/Navigation/Angle-double-down.svg" title="Expand" alt="Expand"/>
          <img v-if="isPanel4Expanded" src="media/svg/icons/Navigation/Angle-double-up.svg" title="Collapse" alt="Collapse"/>
        </b-button>
        Fleet History
      </h3>
      <b-collapse v-model="isPanel4Expanded" id="panel4" class="mb-5">
        <b-card border-variant="primary" header-bg-variant="primary" header-text-variant="white" header-class="p-3 font-size-lg font-weight-bold"
          v-if="appData && appData.transaction">
          <table class="table table-bordered table-sm">
            <thead>
              <th width="10%" class="text-center">Year</th>
              <th width="18%" class="text-center">Comprehensive</th>
              <th width="18%" class="text-center">TPPD</th>
              <th width="18%" class="text-center">Total Fleet Size</th>
              <th width="18%" class="text-center">Comprehensive Excess</th>
              <th width="18%" class="text-center">TPPD Excess</th>
            </thead>
            <tr class="text-center">
              <td class="align-middle">Current&nbsp;Year</td>
              <td><input type="number" class="form-control" :disabled="isReadOnly" v-model="appData.transaction.K_ComprehensiveCurrentYear" @input="calculateFleetHistoryTotals"/></td>
              <td><input type="number" class="form-control" :disabled="isReadOnly" v-model="appData.transaction.K_TppdCurrentYear" @input="calculateFleetHistoryTotals"/></td>
              <td><input type="number" class="form-control" disabled v-model="appData.transaction.K_TotalFleetSizeCurrentYear"/></td>
              <td><b-form-select class="form-control" v-model="appData.transaction.K_ComprehensiveExcessCurrentYear" :options="allowedHistoricalExcesses" text-field="name" value-field="value" :disabled="isReadOnly"></b-form-select></td>
              <td><b-form-select class="form-control" v-model="appData.transaction.K_TppdExcessCurrentYear" :options="allowedHistoricalExcesses" text-field="name" value-field="value" :disabled="isReadOnly"></b-form-select></td>
            </tr>
            <tr class="text-center">
              <td class="align-middle">1&nbsp;Year Prior</td>
              <td><input type="number" class="form-control" :disabled="isReadOnly" v-model="appData.transaction.K_Comprehensive1YearAgo" @input="calculateFleetHistoryTotals"/></td>
              <td><input type="number" class="form-control" :disabled="isReadOnly" v-model="appData.transaction.K_Tppd1YearAgo" @input="calculateFleetHistoryTotals"/></td>
              <td><input type="number" class="form-control" disabled v-model="appData.transaction.K_TotalFleetSize1YearAgo"/></td>
              <td><b-form-select class="form-control" v-model="appData.transaction.K_ComprehensiveExcess1YearAgo" :options="allowedHistoricalExcesses" text-field="name" value-field="value" :disabled="isReadOnly"></b-form-select></td>
              <td><b-form-select class="form-control" v-model="appData.transaction.K_TppdExcess1YearAgo" :options="allowedHistoricalExcesses" text-field="name" value-field="value" :disabled="isReadOnly"></b-form-select></td>
            </tr>
            <tr class="text-center">
              <td class="align-middle">2&nbsp;Years Prior</td>
              <td><input type="number" class="form-control" :disabled="isReadOnly" v-model="appData.transaction.K_Comprehensive2YearsAgo" @input="calculateFleetHistoryTotals"/></td>
              <td><input type="number" class="form-control" :disabled="isReadOnly" v-model="appData.transaction.K_Tppd2YearsAgo" @input="calculateFleetHistoryTotals"/></td>
              <td><input type="number" class="form-control" disabled v-model="appData.transaction.K_TotalFleetSize2YearsAgo"/></td>
              <td><b-form-select class="form-control" v-model="appData.transaction.K_ComprehensiveExcess2YearsAgo" :options="allowedHistoricalExcesses" text-field="name" value-field="value" :disabled="isReadOnly"></b-form-select></td>
              <td><b-form-select class="form-control" v-model="appData.transaction.K_TppdExcess2YearsAgo" :options="allowedHistoricalExcesses" text-field="name" value-field="value" :disabled="isReadOnly"></b-form-select></td>
            </tr>
            <tr class="text-center">
              <td class="align-middle">3&nbsp;Years Prior</td>
              <td><input type="number" class="form-control" :disabled="isReadOnly" v-model="appData.transaction.K_Comprehensive3YearsAgo" @input="calculateFleetHistoryTotals"/></td>
              <td><input type="number" class="form-control" :disabled="isReadOnly" v-model="appData.transaction.K_Tppd3YearsAgo" @input="calculateFleetHistoryTotals"/></td>
              <td><input type="number" class="form-control" disabled v-model="appData.transaction.K_TotalFleetSize3YearsAgo"/></td>
              <td><b-form-select class="form-control" v-model="appData.transaction.K_ComprehensiveExcess3YearsAgo" :options="allowedHistoricalExcesses" text-field="name" value-field="value" :disabled="isReadOnly"></b-form-select></td>
              <td><b-form-select class="form-control" v-model="appData.transaction.K_TppdExcess3YearsAgo" :options="allowedHistoricalExcesses" text-field="name" value-field="value" :disabled="isReadOnly"></b-form-select></td>
            </tr>
            <tr class="text-center">
              <td class="align-middle">4&nbsp;Years Prior</td>
              <td><input type="number" class="form-control" :disabled="isReadOnly" v-model="appData.transaction.K_Comprehensive4YearsAgo" @input="calculateFleetHistoryTotals"/></td>
              <td><input type="number" class="form-control" :disabled="isReadOnly" v-model="appData.transaction.K_Tppd4YearsAgo" @input="calculateFleetHistoryTotals"/></td>
              <td><input type="number" class="form-control" disabled v-model="appData.transaction.K_TotalFleetSize4YearsAgo"/></td>
              <td><b-form-select class="form-control" v-model="appData.transaction.K_ComprehensiveExcess4YearsAgo" :options="allowedHistoricalExcesses" text-field="name" value-field="value" :disabled="isReadOnly"></b-form-select></td>
              <td><b-form-select class="form-control" v-model="appData.transaction.K_TppdExcess4YearsAgo" :options="allowedHistoricalExcesses" text-field="name" value-field="value" :disabled="isReadOnly"></b-form-select></td>
            </tr>
          </table>
        </b-card>
      </b-collapse>
    </ValidationObserver>
    <!-- end: Fleet History -->

    <!-- begin: Claims History -->
    <ValidationObserver ref="claimsHistory">
      <h3 class="font-weight-bold text-dark mt-10">
        <b-button class="btn btn-icon btn-sm ml-1" v-b-toggle="'panel5'">
          <img v-if="!isPanel5Expanded" src="media/svg/icons/Navigation/Angle-double-down.svg" title="Expand" alt="Expand"/>
          <img v-if="isPanel5Expanded" src="media/svg/icons/Navigation/Angle-double-up.svg" title="Collapse" alt="Collapse"/>
        </b-button>
        Claims History
      </h3>
      <b-collapse v-model="isPanel5Expanded" id="panel5" class="mb-5">
        <b-card border-variant="primary" header-bg-variant="primary" header-text-variant="white" header-class="p-3 font-size-lg font-weight-bold"
                v-if="appData && appData.transaction && appData.claimsHistoryInfo"
        >
          <table class="table table-bordered">
            <tr>
              <td width="50%">
                <h4 class="font-weight-bold">
                  Granular Claims History
                  <span v-if="!appData.claimsHistoryInfo.claimsHistoryUploaded && !claimsExtractionRunning" class="text-muted"><i>- Not uploaded</i></span>
                </h4>

                <div v-if="claimsExtractionRunning" class="text-muted">
                  Please wait for the claims history extraction to be completed.
                </div>

                <div v-if="appData.claimsHistoryInfo.claimsHistoryUploaded && !claimsExtractionRunning" class="form-group mb-0">
                  <div v-if="appData.claimsHistoryInfo.K_ClaimsHistoryLink">
                    <a :href="appData.claimsHistoryInfo.K_ClaimsHistoryLink" target="_blank" class="btn btn-primary">View Uploaded Claims History</a>
                  </div>
                  <div v-else>
                    <button class="btn btn-primary" @click.prevent="viewUploadedClaimsHistory" :disabled="appData.claimsHistoryInfo.downloadingClaimsHistory">
                      View Uploaded Claims History
                    </button>
                  </div>
                  <div v-if="!granularClaimsHistoryValid" class="text-danger font-size-h6 mt-5">
                    <strong>Uploaded granular claims history is incomplete for rating. Please re-upload the corrected data.</strong><br/>
                    <span class="font-size-sm">Mandatory fields - [Claim Number, Date of Loss, Claim Excess, Net Incurred]</span>
                  </div>
                </div>

                <div v-if="!claimsExtractionRunning && !isReadOnly && isRenewalTransaction" class="form-group mt-5 mb-0">
                  <button class="btn btn-secondary" @click.prevent="confirmReloadClaimsHistoryFromClaimsCRM">
                    Reload Claims History from Claims CRM
                  </button>
                </div>
              </td>
              <td width="50%">
                <div v-if="!isReadOnly">
                  <claims-extractor @uploadClaimsHistoryFiles="uploadClaimsHistoryFiles" :extraction-job="claimsHistoryExtractionJob"/>
                </div>
              </td>
            </tr>
          </table>

          <div v-if="!claimsExtractionRunning">
            <!-- Start - Claims History Summary Table -->
            <h4 class="font-weight-bold mt-5">
              Claims History Summary
            </h4>
            <table class="table table-bordered table-sm mb-5">
              <thead>
              <th width="8%" class="text-center">Year</th>
              <th width="10%" class="text-center">Working Losses Claims</th>
              <th width="13%" class="text-center">Net Incurred Working Losses</th>
              <th width="10%" class="text-center">Large Losses (>$20,000) Claims</th>
              <th width="13%" class="text-center">Net Incurred Large Losses</th>
              <th width="10%" class="text-center">Natural Peril Losses Claims</th>
              <th width="13%" class="text-center">Net Incurred Natural Peril Losses</th>
              <th width="10%" class="text-center">Total Claims</th>
              <th width="13%" class="text-center">Total Net Incurred</th>
              </thead>
              <tr class="text-center">
                <td class="align-middle">Current&nbsp;Year</td>
                <td class="align-middle"><input type="number" class="form-control" :disabled="isReadOnly" v-model="appData.transaction.K_ClaimsCountWorkingLossesCurrentYear" @input="calculateClaimsHistoryTotals"/></td>
                <td class="align-middle"><currency-input class="form-control" :disabled="isReadOnly" v-model="appData.transaction.K_NetIncurredWorkingLossesCurrentYear" :precision="0" @input="calculateClaimsHistoryTotals"/></td>
                <td class="align-middle"><input type="number" class="form-control" :disabled="isReadOnly" v-model="appData.transaction.K_ClaimsCountLargeLossesCurrentYear" @input="calculateClaimsHistoryTotals"/></td>
                <td class="align-middle"><currency-input class="form-control" :disabled="isReadOnly" v-model="appData.transaction.K_NetIncurredLargeLossesCurrentYear" :precision="0" @input="calculateClaimsHistoryTotals"/></td>
                <td class="align-middle"><input type="number" class="form-control" :disabled="isReadOnly" v-model="appData.transaction.K_ClaimsCountNaturalPerilLossesCurrentYear" @input="calculateClaimsHistoryTotals"/></td>
                <td class="align-middle"><currency-input class="form-control" :disabled="isReadOnly" v-model="appData.transaction.K_NetIncurredNaturalPerilLossesCurrentYear" :precision="0" @input="calculateClaimsHistoryTotals"/></td>
                <td class="align-middle font-weight-bolder" style="background-color: lightgray">{{ appData.transaction.claimsCountCurrentYear }}</td>
                <td class="align-middle font-weight-bolder" style="background-color: lightgray">{{ appData.transaction.niCurrentYear | currency }}</td>
              </tr>
              <tr class="text-center">
                <td class="align-middle">1&nbsp;Year Prior</td>
                <td class="align-middle"><input type="number" class="form-control" :disabled="isReadOnly" v-model="appData.transaction.K_ClaimsCountWorkingLosses1YearAgo" @input="calculateClaimsHistoryTotals"/></td>
                <td class="align-middle"><currency-input class="form-control" :disabled="isReadOnly" v-model="appData.transaction.K_NetIncurredWorkingLosses1YearAgo" :precision="0" @input="calculateClaimsHistoryTotals"/></td>
                <td class="align-middle"><input type="number" class="form-control" :disabled="isReadOnly" v-model="appData.transaction.K_ClaimsCountLargeLosses1YearAgo" @input="calculateClaimsHistoryTotals"/></td>
                <td class="align-middle"><currency-input class="form-control" :disabled="isReadOnly" v-model="appData.transaction.K_NetIncurredLargeLosses1YearAgo" :precision="0" @input="calculateClaimsHistoryTotals"/></td>
                <td class="align-middle"><input type="number" class="form-control" :disabled="isReadOnly" v-model="appData.transaction.K_ClaimsCountNaturalPerilLosses1YearAgo" @input="calculateClaimsHistoryTotals"/></td>
                <td class="align-middle"><currency-input class="form-control" :disabled="isReadOnly" v-model="appData.transaction.K_NetIncurredNaturalPerilLosses1YearAgo" :precision="0" @input="calculateClaimsHistoryTotals"/></td>
                <td class="align-middle font-weight-bolder" style="background-color: lightgray">{{ appData.transaction.claimsCount1YearAgo }}</td>
                <td class="align-middle font-weight-bolder" style="background-color: lightgray">{{ appData.transaction.ni1YearAgo | currency }}</td>
              </tr>
              <tr class="text-center">
                <td class="align-middle">2&nbsp;Years Prior</td>
                <td class="align-middle"><input type="number" class="form-control" :disabled="isReadOnly" v-model="appData.transaction.K_ClaimsCountWorkingLosses2YearsAgo" @input="calculateClaimsHistoryTotals"/></td>
                <td class="align-middle"><currency-input class="form-control" :disabled="isReadOnly" v-model="appData.transaction.K_NetIncurredWorkingLosses2YearsAgo" :precision="0" @input="calculateClaimsHistoryTotals"/></td>
                <td class="align-middle"><input type="number" class="form-control" :disabled="isReadOnly" v-model="appData.transaction.K_ClaimsCountLargeLosses2YearsAgo" @input="calculateClaimsHistoryTotals"/></td>
                <td class="align-middle"><currency-input class="form-control" :disabled="isReadOnly" v-model="appData.transaction.K_NetIncurredLargeLosses2YearsAgo" :precision="0" @input="calculateClaimsHistoryTotals"/></td>
                <td class="align-middle"><input type="number" class="form-control" :disabled="isReadOnly" v-model="appData.transaction.K_ClaimsCountNaturalPerilLosses2YearsAgo" @input="calculateClaimsHistoryTotals"/></td>
                <td class="align-middle"><currency-input class="form-control" :disabled="isReadOnly" v-model="appData.transaction.K_NetIncurredNaturalPerilLosses2YearsAgo" :precision="0" @input="calculateClaimsHistoryTotals"/></td>
                <td class="align-middle font-weight-bolder" style="background-color: lightgray">{{ appData.transaction.claimsCount2YearsAgo }}</td>
                <td class="align-middle font-weight-bolder" style="background-color: lightgray">{{ appData.transaction.ni2YearsAgo | currency }}</td>
              </tr>
              <tr class="text-center">
                <td class="align-middle">3&nbsp;Years Prior</td>
                <td class="align-middle"><input type="number" class="form-control" :disabled="isReadOnly" v-model="appData.transaction.K_ClaimsCountWorkingLosses3YearsAgo" @input="calculateClaimsHistoryTotals"/></td>
                <td class="align-middle"><currency-input class="form-control" :disabled="isReadOnly" v-model="appData.transaction.K_NetIncurredWorkingLosses3YearsAgo" :precision="0" @input="calculateClaimsHistoryTotals"/></td>
                <td class="align-middle"><input type="number" class="form-control" :disabled="isReadOnly" v-model="appData.transaction.K_ClaimsCountLargeLosses3YearsAgo" @input="calculateClaimsHistoryTotals"/></td>
                <td class="align-middle"><currency-input class="form-control" :disabled="isReadOnly" v-model="appData.transaction.K_NetIncurredLargeLosses3YearsAgo" :precision="0" @input="calculateClaimsHistoryTotals"/></td>
                <td class="align-middle"><input type="number" class="form-control" :disabled="isReadOnly" v-model="appData.transaction.K_ClaimsCountNaturalPerilLosses3YearsAgo" @input="calculateClaimsHistoryTotals"/></td>
                <td class="align-middle"><currency-input class="form-control" :disabled="isReadOnly" v-model="appData.transaction.K_NetIncurredNaturalPerilLosses3YearsAgo" :precision="0" @input="calculateClaimsHistoryTotals"/></td>
                <td class="align-middle font-weight-bolder" style="background-color: lightgray">{{ appData.transaction.claimsCount3YearsAgo }}</td>
                <td class="align-middle font-weight-bolder" style="background-color: lightgray">{{ appData.transaction.ni3YearsAgo | currency }}</td>
              </tr>
              <tr class="text-center">
                <td class="align-middle">4&nbsp;Years Prior</td>
                <td class="align-middle"><input type="number" class="form-control" :disabled="isReadOnly" v-model="appData.transaction.K_ClaimsCountWorkingLosses4YearsAgo" @input="calculateClaimsHistoryTotals"/></td>
                <td class="align-middle"><currency-input class="form-control" :disabled="isReadOnly" v-model="appData.transaction.K_NetIncurredWorkingLosses4YearsAgo" :precision="0" @input="calculateClaimsHistoryTotals"/></td>
                <td class="align-middle"><input type="number" class="form-control" :disabled="isReadOnly" v-model="appData.transaction.K_ClaimsCountLargeLosses4YearsAgo" @input="calculateClaimsHistoryTotals"/></td>
                <td class="align-middle"><currency-input class="form-control" :disabled="isReadOnly" v-model="appData.transaction.K_NetIncurredLargeLosses4YearsAgo" :precision="0" @input="calculateClaimsHistoryTotals"/></td>
                <td class="align-middle"><input type="number" class="form-control" :disabled="isReadOnly" v-model="appData.transaction.K_ClaimsCountNaturalPerilLosses4YearsAgo" @input="calculateClaimsHistoryTotals"/></td>
                <td class="align-middle"><currency-input class="form-control" :disabled="isReadOnly" v-model="appData.transaction.K_NetIncurredNaturalPerilLosses4YearsAgo" :precision="0" @input="calculateClaimsHistoryTotals"/></td>
                <td class="align-middle font-weight-bolder" style="background-color: lightgray">{{ appData.transaction.claimsCount4YearsAgo }}</td>
                <td class="align-middle font-weight-bolder" style="background-color: lightgray">{{ appData.transaction.ni4YearsAgo | currency }}</td>
              </tr>
              <tr class="text-center">
                <td class="align-middle font-weight-bolder" style="background-color: lightgray">TOTAL</td>
                <td class="align-middle font-weight-bolder" style="background-color: lightgray">{{ appData.transaction.claimsCountWorkingLossesTotal }}</td>
                <td class="align-middle font-weight-bolder" style="background-color: lightgray">{{ appData.transaction.niWorkingLossesTotal | currency }}</td>
                <td class="align-middle font-weight-bolder" style="background-color: lightgray">{{ appData.transaction.claimsCountLargeLossesTotal }}</td>
                <td class="align-middle font-weight-bolder" style="background-color: lightgray">{{ appData.transaction.niLargeLossesTotal | currency }}</td>
                <td class="align-middle font-weight-bolder" style="background-color: lightgray">{{ appData.transaction.claimsCountNaturalPerilLossesTotal }}</td>
                <td class="align-middle font-weight-bolder" style="background-color: lightgray">{{ appData.transaction.niNaturalPerilLossesTotal | currency }}</td>
                <td class="align-middle font-weight-bolder" style="background-color: lightgray">{{ appData.transaction.claimsCountTotal }}</td>
                <td class="align-middle font-weight-bolder" style="background-color: lightgray">{{ appData.transaction.niTotal | currency }}</td>
              </tr>
            </table>
            <!-- End - Claims History Summary Table -->

            <div class="form-group row mb-0">
              <label class="col-2 col-form-label font-size-lg font-size-h6"><strong>Select Claims History Method For Rating</strong></label>
              <div class="col-5">
                <ValidationProvider rules="required" name="K_ClaimsHistoryInputMethod" v-slot="{ errors }">
                  <b-form-radio-group size="lg" v-model="appData.transaction.K_ClaimsHistoryInputMethod" class="form-control"
                                      @input="claimsHistoryInputTypeChanged" :disabled="isReadOnly">
                    <b-form-radio value="Granular Claims Data"><strong>Granular Claims Data</strong></b-form-radio>
                    <b-form-radio class="ml-2" value="Summary Claims Data"><strong>Summary Claims Data</strong></b-form-radio>
                  </b-form-radio-group>
                  <span class="text-danger"><br>{{ errors[0] }}</span>
                </ValidationProvider>
                <span v-if="appData.transaction.K_ClaimsHistoryInputMethod === 'Summary Claims Data'" class="text-danger">
                <strong>Only 1 quote (for 'Required Excess') can be generated with summary claims data.</strong>
              </span>
              </div>
            </div>

            <div v-if="appData.transaction.K_ClaimsHistoryInputMethod === 'Granular Claims Data'">
              <ValidationProvider :rules="{ 'required-action': { allowFalse: false }}" name="claimsHistoryUploaded" v-slot="{ errors }">
                <input type="checkbox" hidden="hidden" class="form-control" v-model="appData.claimsHistoryInfo.claimsHistoryUploaded"/>
                <span class="text-danger">{{ errors[0] ? 'Granular claims history must be uploaded' : '' }}</span>
              </ValidationProvider>
              <div v-if="appData.claimsHistoryInfo.claimsHistoryUploaded" class="text-danger">
                <span v-if="!granularClaimsHistoryValid">Uploaded granular claims history is incomplete for rating. Please re-upload the corrected data.</span>
                <ValidationProvider :rules="{ 'required-action': { allowFalse: false }}" name="granularClaimsHistoryValid" v-slot="{ errors }">
                  <input type="checkbox" hidden="hidden" class="form-control" v-model="granularClaimsHistoryValid"/>
                  <span class="text-danger"><br>{{ errors[0] }}</span>
                </ValidationProvider>
              </div>
            </div>
          </div>
        </b-card>
      </b-collapse>
    </ValidationObserver>
    <!-- end: Claims History -->

    <!-- begin: Vehicle Types and Postcode Distribution -->
    <ValidationObserver ref="vehicleTypesAndPostcodes">
      <h3 class="font-weight-bold text-dark mt-10">
        <b-button class="btn btn-icon btn-sm ml-1" v-b-toggle="'panel6'">
          <img v-if="!isPanel6Expanded" src="media/svg/icons/Navigation/Angle-double-down.svg" title="Expand" alt="Expand"/>
          <img v-if="isPanel6Expanded" src="media/svg/icons/Navigation/Angle-double-up.svg" title="Collapse" alt="Collapse"/>
        </b-button>
        Vehicle Types & Postcode Distribution
      </h3>
      <b-collapse id="panel6" v-model="isPanel6Expanded" class="mb-5">
        <b-card v-if="appData && appData.transaction"
                border-variant="primary" header-bg-variant="primary" header-text-variant="white" header-class="p-3 font-size-lg font-weight-bold"
        >
          <div v-if="!fleetLookupRunning">
            <strong>
              Click the button below to load the vehicle/postcode summary using the fleet declared on the previous page.
              Optionally, you can manually enter the data in the table below.
            </strong>
            <div class="form-group mt-2">
              <button class="btn btn-primary" @click.prevent="confirmFleetDataLoad" :disabled="isReadOnly">
                Load Data From Fleet Declaration
              </button>
            </div>
          </div>

          <div class="form-group row">
            <label class="col-3 col-form-label font-size-lg font-size-h6"><strong>Select Vehicle Input Method</strong></label>
            <div class="col-6">
              <ValidationProvider rules="required" name="K_VehicleInputMethod" v-slot="{ errors }">
                <b-form-radio-group size="lg" v-model="appData.transaction.K_VehicleInputMethod" class="form-control" :disabled="isReadOnly">
                  <b-form-radio value="Postcode Distribution"><strong>Postcode Distribution</strong></b-form-radio>
                  <b-form-radio class="ml-2" value="Granular Postcode Information"><strong>Granular Postcode Information</strong></b-form-radio>
                </b-form-radio-group>
                <span class="text-danger"><br>{{ errors[0] }}</span>
              </ValidationProvider>
            </div>
          </div>

          <table class="table table-sm table-bordered mt-3" v-if="appData && appData.transaction">
            <thead>
              <th width="25%" class="text-center">Vehicle Type</th>
              <th width="18%" class="text-center">No. of Vehicles</th>
              <th width="17%" class="text-center">Total Market Value</th>
              <th width="15%" class="text-center">Cover Type</th>
              <th v-if="isMethodGranularPostcode" width="10%" class="text-center">State</th>
              <th v-if="isMethodGranularPostcode" width="10%" class="text-center">Postcode</th>
              <th width="5%"></th>
            </thead>

            <tr v-for="(vehicle, index) in appData.transaction.K_VehiclesSummary" :key="index">
              <td>
                <ValidationProvider rules="required" :name="`K_VehicleType${index}`" v-slot="{ errors }">
                  <b-form-select class="form-control" v-model="vehicle.K_VehicleType" :options="refData.vehicleTypes"
                                 text-field="Name" value-field="idObject" size="sm" :disabled="isReadOnly"
                  />
                  <span class="text-danger" v-if="errors.length > 0"><br>{{ errors[0] }}</span>
                </ValidationProvider>
              </td>
              <td>
                <ValidationProvider rules="required" :name="`K_NumberOfVehicles${index}`" v-slot="{ errors }">
                  <input type="number" class="form-control" v-model="vehicle.K_NumberOfVehicles"
                         @input="calculateVehicleTypesTotals" :disabled="isReadOnly"
                  />
                  <span class="text-danger" v-if="errors.length > 0"><br>{{ errors[0] }}</span>
                </ValidationProvider>
              </td>
              <td>
                <ValidationProvider :rules="{'required': vehicle.K_TypeOfCover !== 'Section 2 Only'}" :name="`K_TotalMarketValue${index}`" v-slot="{ errors }">
                  <currency-input
                      class="form-control" v-model="vehicle.K_TotalMarketValue"
                      :allow-negative="currencyConfig.allowNegative" :distraction-free="currencyConfig.distractionFree" :precision="0"
                      @input="calculateVehicleTypesTotals" :disabled="isReadOnly || vehicle.K_TypeOfCover === 'Section 2 Only'"
                  />
                  <span class="text-danger" v-if="errors.length > 0"><br>{{ errors[0] }}</span>
                </ValidationProvider>
              </td>
              <td>
                <ValidationProvider rules="required" :name="`K_TypeOfCover${index}`" v-slot="{ errors }">
                  <b-form-select class="form-control" v-model="vehicle.K_TypeOfCover" size="sm" :disabled="isReadOnly"
                    @input="coverTypeChanged(vehicle)" >
                    <option value="Section 1 & 2">Section 1 & 2</option>
                    <option value="Section 2 Only">Section 2 Only</option>
                    <option v-if="isRenewalTransaction" value="Static Risk">Static Risk</option>
                  </b-form-select>
                  <span class="text-danger" v-if="errors.length > 0"><br>{{ errors[0] }}</span>
                </ValidationProvider>
              </td>
              <td v-if="isMethodGranularPostcode">
                <ValidationProvider rules="required" :name="`K_State${index}`" v-slot="{ errors }">
                  <b-form-select class="form-control" v-model="vehicle.K_State" size="sm" :disabled="isReadOnly">
                    <option value="NSW">NSW</option>
                    <option value="VIC">VIC</option>
                    <option value="ACT">ACT</option>
                    <option value="QLD">QLD</option>
                    <option value="WA">WA</option>
                    <option value="SA">SA</option>
                    <option value="NT">NT</option>
                    <option value="TAS">TAS</option>
                    <option value="NZ">NZ</option>
                  </b-form-select>
                  <span class="text-danger" v-if="errors.length > 0"><br>{{ errors[0] }}</span>
                </ValidationProvider>
              </td>
              <td v-if="isMethodGranularPostcode">
                <ValidationProvider rules="required|numeric" :name="`K_Postcode${index}`" v-slot="{ errors }">
                  <input type="text" class="form-control" v-model="vehicle.K_Postcode" maxlength="4" minlength="4"
                         :disabled="isReadOnly"/>
                  <span class="text-danger" v-if="errors.length > 0"><br>{{ errors[0] }}</span>
                </ValidationProvider>
              </td>
              <td>
                <button class="btn btn-sm btn-outline-danger" title="Delete" @click.prevent="deleteVehicleTypeRow(index)" :disabled="isReadOnly">
                  <i class="fa fa-trash-alt"></i>
                </button>
              </td>
            </tr>
            <tr>
              <button class="btn btn-primary m-2" @click.prevent="addVehicleTypeRow()" :disabled="isReadOnly">
                Add Row
              </button>
              <ValidationProvider :rules="{ 'required-action': { allowFalse: false }}" name="atleast1VehicleTypeAdded" v-slot="{ errors }">
                <input type="number" hidden="hidden" class="form-control" v-model="atleast1VehicleTypeAdded"
                       :disabled="isReadOnly"/>
                <span class="text-danger">{{ errors[0] }}</span>
              </ValidationProvider>
            </tr>

            <tr>
              <td></td>
              <td class="align-middle">
                <strong>Total:<br />{{ appData.transaction.K_TotalNumberOfVehicles }}</strong>
              </td>
              <td class="align-middle">
                <strong v-if="appData.transaction.K_AverageTotalMarketValue">
                  Average:<br />{{ appData.transaction.K_AverageTotalMarketValue | currency }}
                </strong>
              </td>
              <td></td>
              <td></td>
            </tr>
          </table>

          <div v-if="!isMethodGranularPostcode">
            <br/>
            <h5>Postcode Distribution</h5>
            <div>Enter the postcode distribution for the above vehicles. <b>For New Zealand, enter 'NZ' as the postcode.</b></div>

            <table class="table table-sm table-bordered mt-3 col-4" v-if="appData && appData.transaction">
              <thead>
              <th width="40%" class="text-center">Postcode</th>
              <th v-if="showPdPercentColumn" width="60%" class="text-center">Percentage of Vehicles</th>
              <th v-if="!showPdPercentColumn" width="60%" class="text-center">Number of Vehicles</th>
              </thead>

              <tr v-for="(pCode, index) in appData.transaction.K_PostcodeDistributionJson" :key="index">
                <td>
                  <ValidationProvider rules="required" :name="`pcode${index}.postcode`" v-slot="{ errors }">
                    <input type="text" class="form-control" v-model="pCode.postcode" maxlength="4"
                           @input="calculatePostcodeDistributionTotal" :disabled="isReadOnly"/>
                    <span class="text-danger">{{ errors[0] }}</span>
                  </ValidationProvider>
                </td>
                <td v-if="showPdPercentColumn">
                  <ValidationProvider rules="required|min_value:0.1|max_value:100" :name="`pcode${index}.percentage`" v-slot="{ errors }">
                    <b-input-group append="%">
                      <input type="number" min="1" max="100" class="form-control" v-model="pCode.percentage"
                             @input="calculatePostcodeDistributionTotal" :disabled="isReadOnly"/>
                    </b-input-group>
                    <span class="text-danger">{{ errors[0] }}</span>
                  </ValidationProvider>
                </td>
                <td v-if="!showPdPercentColumn">
                  <ValidationProvider rules="required" :name="`pcode${index}.numberOfVehicles`" v-slot="{ errors }">
                    <input type="number" class="form-control" v-model="pCode.numberOfVehicles"
                           @input="calculatePostcodeDistributionTotal" :disabled="isReadOnly"/>
                    <span class="text-danger">{{ errors[0] }}</span>
                  </ValidationProvider>
                </td>
                <td>
                  <button class="btn btn-sm btn-outline-danger" @click.prevent="deletePostcodeEntry(index)" title="Delete" v-if="!isReadOnly">
                    <i class="fa fa-trash-alt"></i>
                  </button>
                </td>
              </tr>
              <tr>
                <td>
                <button class="btn btn-primary m-2" :disabled="isReadOnly" @click.prevent="addPostcodeEntry">
                  Add Row
                </button>
                <ValidationProvider :rules="{ 'required-action': { allowFalse: false }}" name="atleast1PostcodeAdded" v-slot="{ errors }">
                  <input type="number" hidden="hidden" class="form-control" v-model="atleast1PostcodeAdded"
                         :disabled="isReadOnly"/>
                  <span class="text-danger">{{ errors[0] }}</span>
                </ValidationProvider>
                </td>
                <td>
                <span class="col-9 text-right">
                  <label class="col-form-label"><strong> Total: </strong></label>
                </span>
                  <span class="col-3 pt-3" v-if="showPdPercentColumn">
                  <strong>{{ appData.transaction.pdTotal }} %</strong>
                  <ValidationProvider rules="required|totalPercentage" name="pdTotal" v-slot="{ errors }">
                    <input type="number" v-model="appData.transaction.pdTotal" hidden/>
                    <span class="text-danger"><br>{{ errors[0] }}</span>
                  </ValidationProvider>
                </span>
                  <span class="col-3 pt-3" v-if="!showPdPercentColumn">
                  <strong>{{ appData.transaction.pdVehNumTotal }}</strong>
                  <ValidationProvider rules="required" name="pdVehNumTotal" v-slot="{ errors }">
                    <input type="number" v-model="appData.transaction.pdVehNumTotal" hidden/>
                    <span class="text-danger"><br>{{ errors[0] }}</span>
                  </ValidationProvider>
                </span>
                </td>
              </tr>
            </table>
          </div>
        </b-card>
      </b-collapse>
    </ValidationObserver>
    <!-- end: Vehicle Types and Postcode Distribution -->

    <!-- begin: Rating Questions -->
    <ValidationObserver ref="ratingQuestions">
      <h3 class="font-weight-bold text-dark mt-10">
        <b-button class="btn btn-icon btn-sm ml-1" v-b-toggle="'panel7'">
          <img v-if="!isPanel7Expanded" src="media/svg/icons/Navigation/Angle-double-down.svg" title="Expand" alt="Expand"/>
          <img v-if="isPanel7Expanded" src="media/svg/icons/Navigation/Angle-double-up.svg" title="Collapse" alt="Collapse"/>
        </b-button>
        Rating Questions
      </h3>
      <b-collapse v-model="isPanel7Expanded" id="panel7" class="mb-5">
        <b-card border-variant="primary" header-bg-variant="primary" header-text-variant="white" header-class="p-3 font-size-lg font-weight-bold"
                v-if="appData && appData.transaction"
        >
          <strong>
            Please provide answers to the rating questions below.
          </strong><br/>

          <div class="form-group row mb-1 mt-5">
            <label class="col-3 col-form-label text-right">Claims as at Date</label>
            <div class="col-3">
              <ValidationProvider rules="required" name="K_ClaimsAsAtDate" v-slot="{ errors }">
                <b-form-datepicker class="form-control" v-model="appData.transaction.K_ClaimsAsAtDate"
                    :date-format-options="{ year: 'numeric', month: '2-digit', day: '2-digit'}" locale="en-AU"
                    :disabled="isReadOnly"
                ></b-form-datepicker>
                <span class="text-danger">{{ errors[0] }}</span>
              </ValidationProvider>
            </div>

            <label class="col-3 col-form-label text-right">Avg Reporting Delay</label>
            <div class="col-3 ">
              <ValidationProvider rules="required" name="K_AverageReportingDelay" v-slot="{ errors }">
                <b-form-select class="form-control" v-model="appData.transaction.K_AverageReportingDelay" :disabled="isReadOnly">
                  <option></option>
                  <option value="<1">Less than 1</option>
                  <option value="1-10">1-10</option>
                  <option value="11-20">11-20</option>
                  <option value="21-30">21-30</option>
                  <option value="31-40">31-40</option>
                  <option value="41-60">41-60</option>
                  <option value="61-80">61-80</option>
                  <option value="81-100">81-100</option>
                  <option value="101-120">101-120</option>
                  <option value="121-140">121-140</option>
                  <option value="141-160">141-160</option>
                  <option value="161-180">161-180</option>
                  <option value="181-200">181-200</option>
                  <option value="200+">200+</option>
                </b-form-select>
                <span class="text-danger">{{ errors[0] }}</span>
              </ValidationProvider>
            </div>
          </div>

          <div class="form-group row mb-1">
            <label class="col-3 col-form-label text-right">Total Commission %</label>
            <div class="col-3">
              <ValidationProvider rules="required|min_value:0|max_value:100" name="K_TotalCommissionPercent" v-slot="{ errors }">
                <input type="number" class="form-control" min="0" max="100"
                       v-model="appData.transaction.K_TotalCommissionPercent" :disabled="isReadOnly">
                <span class="text-danger">{{ errors[0] }}</span>
                <span class="text-warning" v-if="showZeroCommissionWarning">Warning - Zero commission % entered</span>
              </ValidationProvider>
            </div>

            <label class="col-3 col-form-label text-right">Broker Commission %</label>
            <div class="col-3">
              <ValidationProvider rules="required|min_value:0|max_value:100" name="K_BrokerCommissionPercent" v-slot="{ errors }">
                <input type="number" class="form-control" min="0" max="100"
                       v-model="appData.transaction.K_BrokerCommissionPercent" :disabled="isReadOnly">
                <span class="text-danger">{{ errors[0] }}</span>
                <span class="text-danger" v-if="showBrokerCommissionError">Broker Commission % cannot be less than Total Commission %.</span>
              </ValidationProvider>
            </div>
          </div>

          <div class="form-group row mb-1">
            <label class="col-3 col-form-label text-right">Other Vehicle Basis</label>
            <div class="col-3">
              <ValidationProvider rules="required" name="K_OtherBasis" v-slot="{ errors }">
                <b-form-select class="form-control" v-model="appData.transaction.K_OtherBasis"
                               :options="refData.vehicleTypes" text-field="Name" value-field="Name" :disabled="isReadOnly"
                >
                </b-form-select>
                <span class="text-danger">{{ errors[0] }}</span>
              </ValidationProvider>
            </div>
          </div>
        </b-card>
      </b-collapse>
    </ValidationObserver>
    <!-- end: Rating Questions -->

    <div class="d-flex justify-content-center mt-5" v-if="!isReadOnly">
      <button class="btn btn-lg btn-primary" title="Calculate Premiums" @click.prevent="calculatePremiums">
        Calculate Premiums
      </button>
      <button class="btn btn-lg btn-outline-primary ml-5" title="Save page PDF" @click.prevent="savePremiumPage">
        Save Page PDF
      </button>
    </div>

      <!-- begin: UW Approvals -->
    <div v-if="quote0Valid">
      <ValidationObserver ref="approvals">
      <h3 class="font-weight-bold text-dark mt-10">
        <b-button class="btn btn-icon btn-sm ml-1" v-b-toggle="'approvalsPanel'">
          <img v-if="!isApprovalsPanelExpanded" src="media/svg/icons/Navigation/Angle-double-down.svg" title="Expand" alt="Expand"/>
          <img v-if="isApprovalsPanelExpanded" src="media/svg/icons/Navigation/Angle-double-up.svg" title="Collapse" alt="Collapse"/>
        </b-button>
        Underwriting Approvals
      </h3>
      <b-collapse v-model="isApprovalsPanelExpanded" id="approvalsPanel" class="mb-5">
        <b-card border-variant="primary" header-bg-variant="primary" header-text-variant="white" header-class="p-3 font-size-lg font-weight-bold"
                v-if="appData"
        >

          <div v-if="!appData.uwApprovalsDataValid">
            <span class="text-danger">Invalid approvals data - Please click on 'Refresh Approvals' button to get the latest data.</span>
          </div>

          <div class="d-flex justify-content-center mb-10" v-if="!isReadOnly">
            <button class="btn btn-lg btn-primary" title="Refresh Approvals" @click.prevent="refreshUwApprovals">
              Refresh Approvals
            </button>
          </div>

          <div v-if="appData.uwApprovalsDataValid && (!Array.isArray(appData.uwApprovals) || appData.uwApprovals.length === 0)">
            No approvals pending.
          </div>

          <table class="table table-sm table-bordered mt-3" v-if="Array.isArray(appData.uwApprovals) && appData.uwApprovals.length > 0">
            <thead>
            <th width="30%" class="text-center">Approval Message</th>
            <th width="10%" class="text-center">Related Quote</th>
            <th width="10%" class="text-center">Eligible Approvers</th>
            <th width="10%" class="text-center">Approval Status</th>
            <th width="10%" class="text-center">Actioned By</th>
            <th width="10%" class="text-center">Action Time</th>
            <th width="20%" class="text-center">Actions / Comments</th>
            </thead>

            <tr v-for="(approval, index) in appData.uwApprovals" :key="index">
              <td class="align-middle">
                {{ approval.K_ApprovalMessage }}
              </td>
              <td class="align-middle">
                {{ approval.K_Quote ? getQuoteExcess(approval.K_Quote.id) : null }}
              </td>
              <td class="align-middle">
                {{ approval.K_Approvers.join(' / ') }}
              </td>
              <td class="text-center">
                <b-badge :variant="getApprovalStatusVariant(approval.K_ApprovalStatus)" class="mt-2">{{approval.K_ApprovalStatus}}</b-badge>
              </td>
              <td class="align-middle">
                {{ approval.K_ActionedBy }}
              </td>
              <td class="align-middle">
                <span v-if="approval.K_ActionTime">{{ approval.K_ActionTime | dateParse() | dateFormat("DD/MM/YYYY h:mm A") }}</span>
              </td>
              <td>
              <span v-if="!isReadOnly && approval.K_ApprovalStatus === 'Awaiting Approval' && isUserEligibleForApproval(approval)">
                <button class="btn btn-sm btn-success" title="Approve" @click.prevent="confirmApprove(approval)">Approve</button>
                <button class="btn btn-sm btn-danger ml-2" title="Reject" @click.prevent="confirmReject(approval)">Reject</button>
              </span>
                <span v-if="approval.K_ApprovalStatus !== 'Awaiting Approval'">
                {{ approval.K_Comments }}
              </span>
              </td>
            </tr>
          </table>
          <b-modal ref="approveModal" title="Confirm Approval" size="md" scrollable no-close-on-backdrop hide-header-close
                   @ok="approveApproval" ok-title="Approve" ok-variant="success"
          >
            <div v-if="approvalBeingActioned">
              <b-card-text class="font-weight-bold font-size-h5">{{ approvalBeingActioned.K_ApprovalMessage }}</b-card-text>
              <div class="mt-5">
                <b-card-text>Comments (Optional)</b-card-text>
                <div class="form-group">
                  <b-form-textarea class="form-control" v-model="approvalBeingActioned.K_Comments" rows="3"></b-form-textarea>
                </div>
              </div>
              <div class="mt-4">
                <b-card-text>Attachments (Optional)</b-card-text>
                <div class="form-group">
                  <approval-attachments :approval="approvalBeingActioned" :is-read-only="isReadOnly"/>
                </div>
              </div>
              <b-card-text v-if="rejectionApprovalModalError" class="text-danger mt-3">{{ rejectionApprovalModalError }}</b-card-text>
            </div>
          </b-modal>

          <b-modal ref="rejectModal" title="Confirm Rejection" size="md" scrollable no-close-on-backdrop hide-header-close
                   @ok="rejectApproval" ok-title="Reject" ok-variant="danger"
          >
            <div v-if="approvalBeingActioned">
              <b-card-text class="font-weight-bold font-size-h5">{{ approvalBeingActioned.K_ApprovalMessage }}</b-card-text>
              <div class="mt-5">
                <b-card-text>Comments</b-card-text>
                <div class="form-group">
                  <b-form-textarea class="form-control" v-model="approvalBeingActioned.K_Comments" rows="3" size="2000"></b-form-textarea>
                </div>
              </div>
              <div class="mt-4">
                <b-card-text>Attachments (Optional)</b-card-text>
                <div class="form-group">
                  <approval-attachments :approval="approvalBeingActioned" :is-read-only="isReadOnly"/>
                </div>
              </div>
              <b-card-text v-if="rejectionApprovalModalError" class="text-danger mt-3">{{ rejectionApprovalModalError }}</b-card-text>
            </div>
          </b-modal>
        </b-card>
      </b-collapse>
    </ValidationObserver>
    </div>
    <!-- end: UW Approvals -->

    <!-- begin: UW Approvals Validation -->
    <div>
      <ValidationProvider :rules="{ 'required': { allowFalse: false }}" name="uwApprovalsCompleted" v-slot="{ errors }">
        <input type="checkbox" hidden="hidden" class="form-control" v-model="uwApprovalsCompleted"/>
        <span class="text-danger">{{ errors[0] ? 'All Approvals must be completed before proceeding further.' : '' }}</span>
      </ValidationProvider>
      <br/>
      <ValidationProvider :rules="{ 'required': { allowFalse: false }}" name="uwApprovalsDataValid" v-slot="{ errors }">
        <input type="checkbox" hidden="hidden" class="form-control" v-model="appData.uwApprovalsDataValid"/>
        <span class="text-danger">{{ errors[0] ? 'Invalid approvals data - Please click on \'Refresh Approvals\' button to get the latest data.' : '' }}</span>
      </ValidationProvider>
    </div>
    <!-- end: UW Approvals Validation -->

    <div v-if="quote0Valid">
      <h3 class="font-weight-bold text-dark mb-5">
        <b-button class="btn btn-icon btn-sm ml-1" v-b-toggle="'quote0Panel'">
          <img v-if="!isQuote0PanelExpanded" src="media/svg/icons/Navigation/Angle-double-down.svg" title="Expand" alt="Expand"/>
          <img v-if="isQuote0PanelExpanded" src="media/svg/icons/Navigation/Angle-double-up.svg" title="Collapse" alt="Collapse"/>
        </b-button>
        Quote Output - <b-badge variant="primary">{{ this.getQuoteTitle(appData.quotes[0]) }}</b-badge>
        <b-spinner v-if="appData.quotes[0].calculatingPremiums" class="ml-2 align-middle" variant="primary"></b-spinner>
      </h3>
      <b-collapse v-model="isQuote0PanelExpanded" id="quote0Panel" class="mb-5">
        <Quote ref="quote0Component" :quote="appData.quotes[0]" :vehicle-types="refData.vehicleTypes" :is-renewal="isRenewalTransaction"
               :is-read-only="isReadOnly || appData.quotes[0].calculatingPremiums" :show-copy-fees-button="appData.quotes.length>1"
               v-on="$listeners">
        </Quote>
      </b-collapse>
    </div>

    <div v-if="quote1Valid">
      <h3 class="font-weight-bold text-dark mb-5">
        <b-button class="btn btn-icon btn-sm ml-1" v-b-toggle="'quote1Panel'">
          <img v-if="!isQuote1PanelExpanded" src="media/svg/icons/Navigation/Angle-double-down.svg" title="Expand" alt="Expand"/>
          <img v-if="isQuote1PanelExpanded" src="media/svg/icons/Navigation/Angle-double-up.svg" title="Collapse" alt="Collapse"/>
        </b-button>
        Quote Output - <b-badge variant="primary">{{ this.getQuoteTitle(appData.quotes[1]) }}</b-badge>
        <b-spinner v-if="appData.quotes[1].calculatingPremiums" class="ml-2 align-middle" variant="primary"></b-spinner>
      </h3>
      <b-collapse v-model="isQuote1PanelExpanded" id="quote1Panel" class="mb-5">
        <Quote ref="quote1Component" :quote="appData.quotes[1]" :vehicle-types="refData.vehicleTypes" :is-renewal="isRenewalTransaction"
               :is-read-only="isReadOnly || appData.quotes[1].calculatingPremiums" :show-copy-fees-button="appData.quotes.length>1"
               v-on="$listeners">
        </Quote>
      </b-collapse>
    </div>

    <div v-if="quote2Valid">
      <h3 class="font-weight-bold text-dark mb-5">
        <b-button class="btn btn-icon btn-sm ml-1" v-b-toggle="'quote2Panel'">
          <img v-if="!isQuote2PanelExpanded" src="media/svg/icons/Navigation/Angle-double-down.svg" title="Expand" alt="Expand"/>
          <img v-if="isQuote2PanelExpanded" src="media/svg/icons/Navigation/Angle-double-up.svg" title="Collapse" alt="Collapse"/>
        </b-button>
        Quote Output - <b-badge variant="primary">{{ this.getQuoteTitle(appData.quotes[2]) }}</b-badge>
        <b-spinner v-if="appData.quotes[2].calculatingPremiums" class="ml-2 align-middle" variant="primary"></b-spinner>
      </h3>
      <b-collapse v-model="isQuote2PanelExpanded" id="quote2Panel" class="mb-5">
        <Quote ref="quote2Component" :quote="appData.quotes[2]" :vehicle-types="refData.vehicleTypes" :is-renewal="isRenewalTransaction"
               :is-read-only="isReadOnly || appData.quotes[2].calculatingPremiums" :show-copy-fees-button="appData.quotes.length>1"
               v-on="$listeners">
        </Quote>
      </b-collapse>
    </div>

    <ValidationProvider :rules="{ 'required': { allowFalse: false }}" name="atleast1QuoteExists" v-slot="{ errors }">
      <input type="checkbox" hidden="hidden" class="form-control" v-model="atleast1QuoteExists"/>
      <span class="text-danger">{{ errors[0] ? 'At least 1 quote must be generated.' : '' }}</span>
    </ValidationProvider>

  </ValidationObserver>
</template>

<style lang="scss">

body.swal2-shown > [aria-hidden="true"] {
  transition: 0.1s filter;
  filter: blur(10px);
}

</style>nb

<script>
import _ from "lodash";
import Swal from "sweetalert2";
import ApprovalAttachments from "@/view/pages/wizard/newbusiness-renewal/components/ApprovalAttachments.vue";
import Quote from "@/view/pages/wizard/newbusiness-renewal/components/Quote.vue";
import ClaimsExtractor from "@/view/pages/wizard/components/ClaimsExtractor.vue";

export default {
  name: "PremiumPage",
  components: {ClaimsExtractor, ApprovalAttachments, Quote },
  props: {
    appData: Object,
    refData: Object,
    expandAllSections: Boolean,
    isReadOnly: Boolean
  },

  mounted() {
    if(this.expandAllSections) {
      this.isPanel1Expanded = true;
      this.isPanel2Expanded = true;
      this.isPanel3Expanded = true;
      this.isPanel4Expanded = true;
      this.isPanel5Expanded = true;
      this.isPanel6Expanded = true;
      this.isPanel7Expanded = true;
      this.isApprovalsPanelExpanded = true;
      this.isQuote0PanelExpanded = true;
      this.isQuote1PanelExpanded = true;
      this.isQuote2PanelExpanded = true;
    }
    this.refreshClaimsHistoryStatus(true);
  },

  data() {
    return {
      isPanel1Expanded: false,
      isPanel2Expanded: false,
      isPanel3Expanded: false,
      isPanel4Expanded: false,
      isPanel5Expanded: false,
      isPanel6Expanded: false,
      isPanel7Expanded: false,
      isApprovalsPanelExpanded: false,
      isQuote0PanelExpanded: false,
      isQuote1PanelExpanded: false,
      isQuote2PanelExpanded: false,

      approvalBeingActioned: null,
      rejectionApprovalModalError: null,

      currencyConfig: {
        allowNegative: false,
        distractionFree: false,
        precision: 2
      }
    };
  },

  computed: {
    claimsExtractionRunning: function () {
      return this.appData?.claimsHistoryInfo?.K_ClaimsHistoryExtractionRunning || false;
    },

    claimsHistoryExtractionJob: function () {
      return this.appData?.claimsHistoryInfo?.claimsExtractionJobInfo || null;
    },

    isRenewalTransaction: function() {
      const txnType = _.get(this.appData, 'transaction.K_Type');
      return txnType === 'Renewal';
    },

    isBrandFuse: function () {
      const brand = _.get(this.appData, 'transaction.K_Brand');
      return brand === 'Fuse' || brand === 'Fuse MSI';
    },

    atleast1BusinessActivitySelected: function() {
      if(this.appData) {
        return this.appData.transaction.K_BusinessActivitiesJson.length > 0;
      }
      return false;
    },

    fleetLookupRunning: function () {
      return this.appData?.transaction?.K_FleetLookupRunning || false;
    },

    totalVehicleUsePercent: function() {
      let total = 0;
      if(this.appData) {
        for (const key in this.appData.transaction.K_VehicleUseJson) {
          total += parseFloat(this.appData.transaction.K_VehicleUseJson[key]) || 0;
        }
      }
      return total;
    },

    atleast1VehicleTypeAdded: function (){
      if(this.appData) {
        return this.appData.transaction.K_VehiclesSummary.length > 0;
      }
      return false;
    },

    atleast1PostcodeAdded: function (){
      if(this.appData) {
        return this.appData.transaction.K_PostcodeDistributionJson.length > 0;
      }
      return false;
    },

    showPdPercentColumn: function () {
      if (this.appData && this.appData.transaction) {
        const pdJson = this.appData.transaction.K_PostcodeDistributionJson;
        if (Array.isArray(pdJson) && pdJson.length > 0) {
          return _.some(pdJson, pd => {
            return !!pd.percentage;
          });
        }
      }
      return false;
    },

    showZeroCommissionWarning: function () {
      if(!_.isNil(this.appData.transaction.K_TotalCommissionPercent) && this.appData.transaction.K_TotalCommissionPercent) {
        return Number(this.appData.transaction.K_TotalCommissionPercent) === 0;
      }
      return false;
    },

    showBrokerCommissionError: function () {
      const totalCommissionPercent = !_.isNil(this.appData.transaction.K_TotalCommissionPercent) ? Number(this.appData.transaction.K_TotalCommissionPercent) : 0;
      const brokerCommissionPercent = !_.isNil(this.appData.transaction.K_BrokerCommissionPercent) ? Number(this.appData.transaction.K_BrokerCommissionPercent) : 0;
      return brokerCommissionPercent > totalCommissionPercent;
    },

    isMethodGranularPostcode: function() {
      if(this.appData) {
        return this.appData.transaction.K_VehicleInputMethod === 'Granular Postcode Information';
      }
      return false;
    },

    allowedExcesses: function () {
      const formatter = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' });

      // Fuse product floor is $1,000; other brands stay at the Finrater-safe $1,200 floor.
      const minExcess = this.isBrandFuse ? 1000 : 1200;
      const excesses = [{ name: '', value: '' }];
      for(let i=minExcess; i<=50000; i+=100) {
        excesses.push({ name: formatter.format(i), value: String(i)});
      }
      return excesses;
    },

    allowedHistoricalExcesses: function () {
      const formatter = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' });

      // History excess feeds Finrater Excess_CY* tables that have no column below 1200.
      // Quote excess for Fuse may be $1,000 via allowedExcesses; keep history at the table floor.
      const minExcess = 1200;
      const excesses = [{ name: '', value: '' }];
      for(let i=minExcess; i<=50000; i+=100) {
        excesses.push({ name: formatter.format(i), value: String(i)});
      }
      return excesses;
    },

    uwApprovalsCompleted: function() {
      if(this.appData) {
        const approvals = this.appData.uwApprovals;
        if(Array.isArray(approvals) && approvals.length > 0) {
          return !_.some(approvals, approval => {
            return approval.K_ApprovalStatus !== 'Approved';
          })
        }
        else {
          return true;
        }
      }
      return false;
    },

    alternateExcess1Enabled: function () {
      if(this.appData) {
        return !!this.appData.transaction.K_RequiredExcess
            && this.appData.transaction.K_ClaimsHistoryInputMethod !== 'Summary Claims Data';
      }
      return false;
    },

    alternateExcess2Enabled: function () {
      if(this.appData) {
        return this.alternateExcess1Enabled
            && !!this.appData.transaction.K_AlternateExcess1
      }
      return false;
    },

    excessesValid: function() {
      if(this.appData) {
        const excesses = [];
        const requiredExcess = _.get(this.appData, 'transaction.K_RequiredExcess');
        if(requiredExcess) {
          excesses.push(requiredExcess);
        }
        const alternateExcess1 = _.get(this.appData, 'transaction.K_AlternateExcess1');
        if(alternateExcess1) {
          excesses.push(alternateExcess1);
        }
        const alternateExcess2 = _.get(this.appData, 'transaction.K_AlternateExcess2');
        if(alternateExcess2) {
          excesses.push(alternateExcess2);
        }

        // Ensure that all excesses are different
        if(excesses.length > 1) {
          return new Set(excesses).size === excesses.length;
        }
      }
      return true;
    },

    quote0Valid: function () {
      return this.appData && this.appData.transaction.K_RequiredExcess
          && Array.isArray(this.appData.quotes) && _.nth(this.appData.quotes, 0);
    },

    quote1Valid: function () {
      return this.quote0Valid
          && this.appData.transaction.K_AlternateExcess1 && _.nth(this.appData.quotes, 1);
    },

    quote2Valid: function () {
      return this.quote1Valid
          && this.appData.transaction.K_AlternateExcess2 && _.nth(this.appData.quotes, 2);
    },

    atleast1QuoteExists: function () {
      return this.quote0Valid || this.quote1Valid || this.quote2Valid;
    },

    fleetIncomplete: function () {
      if(this.appData) {
        return this.appData.fleetInfo.incompleteCount > 0;
      }
      return true;
    },

    granularClaimsHistoryValid: function () {
      if(this.appData && this.appData.claimsHistoryInfo) {
        return !this.appData.claimsHistoryInfo.K_ClaimsHistoryInvalid;
      }
      return true;
    }
  },

  methods: {
    addBusinessActivity: function () {
      this.appData.transaction.K_BusinessActivitiesJson.push({});
      this.calculateBusinessActivityPercentTotal();
    },

    deleteBusinessActivity: function (index) {
      this.appData.transaction.K_BusinessActivitiesJson.splice(index, 1);
      this.calculateBusinessActivityPercentTotal();
    },

    coverTypeChanged: function (vehicle) {
      if(vehicle.K_TypeOfCover === 'Section 2 Only') {
        this.$set(vehicle, 'K_TotalMarketValue', null);
        this.calculateVehicleTypesTotals();
      }
    },

    deleteVehicleTypeRow: function(index) {
      if (this.appData && this.appData.transaction && Array.isArray(this.appData.transaction.K_VehiclesSummary)) {
        let vehiclesSummary = this.appData.transaction.K_VehiclesSummary;

        if (index < vehiclesSummary.length) {
          vehiclesSummary.splice(index, 1);
          this.appData.transaction.K_VehiclesSummary = vehiclesSummary;
        }
        this.calculateVehicleTypesTotals();
      }
    },

    addVehicleTypeRow: function() {
      if (this.appData && this.appData.transaction) {
        let vehiclesSummary = this.appData.transaction.K_VehiclesSummary;
        if (!vehiclesSummary) {
          vehiclesSummary = [];
        }
        vehiclesSummary.push({});
        this.appData.transaction.K_VehiclesSummary = vehiclesSummary;
        this.calculateVehicleTypesTotals();
      }
    },

    addPostcodeEntry: function () {
      this.appData.transaction.K_PostcodeDistributionJson.push({});
      this.calculatePostcodeDistributionTotal();
    },

    deletePostcodeEntry: function (index) {
      this.appData.transaction.K_PostcodeDistributionJson.splice(index, 1);
      this.calculatePostcodeDistributionTotal();
    },

    calculateFleetHistoryTotals: function() {
      this.$emit('calculateFleetHistoryTotals');
    },

    viewUploadedClaimsHistory: function () {
      this.$emit('viewUploadedClaimsHistory');
    },

    refreshClaimsHistoryStatus: function (silent) {
      this.$emit('refreshClaimsHistoryStatus', silent);
    },

    uploadClaimsHistoryFiles: function (selectedFiles) {
      this.$emit('uploadClaimsHistoryFiles', selectedFiles);
    },

    confirmReloadClaimsHistoryFromClaimsCRM: function () {
      Swal.fire({
        titleText: `Reload Claims History?`,
        html: `Are you sure you want to reload the claims history from the Claims CRM?`,
        icon: "warning",
        allowOutsideClick: false,
        allowEnterKey: false,
        confirmButtonText: 'Yes',
        showConfirmButton: true,
        showCancelButton: true,
        showCloseButton: false
      })
          .then(result => {
            if(result && result.isConfirmed) {
              this.$emit('reloadClaimsHistoryFromClaimsCRM');
            }
          });
    },

    calculateVehicleTypesTotals: function() {
      this.$emit('calculateVehicleTypesTotals');
    },

    calculateBusinessActivityPercentTotal: function() {
      this.$emit('calculateBusinessActivityPercentTotal');
    },

    calculatePostcodeDistributionTotal: function () {
      this.$emit('calculatePostcodeDistributionTotal');
    },

    calculateClaimsHistoryTotals: function () {
      this.$emit('calculateClaimsHistoryTotals');
    },

    paymentMethodChanged: function () {
      if (this.appData && this.appData.transaction) {
        const paymentMethod = this.appData.transaction.K_PaymentMethod;

        if(paymentMethod === 'Monthly Premium') {
          this.$set(this.appData.transaction, 'K_EndorsementFee', null);
        }
        else if(paymentMethod === 'Annual Premium') {
          this.$set(this.appData.transaction, 'K_MonthlyFee', null);
        }
      }
    },

    claimsHistoryInputTypeChanged: function () {
      const chInputMethod = _.get(this.appData, 'transaction.K_ClaimsHistoryInputMethod');
      if(chInputMethod === 'Summary Claims Data') {
        this.appData.transaction.K_AlternateExcess1 = null;
        this.appData.transaction.K_AlternateExcess2 = null;
      }
    },

    calculatePremiums: async function() {
      // Validate rating input data
      let refs = ['clientBusinessDetails','requirementsOfPolicy','fleetHistory', 'claimsHistory', 'vehicleTypesAndPostcodes', 'ratingQuestions'];
      let isRatingInputValid = true;
      for await(let ref of refs) {
        isRatingInputValid = isRatingInputValid && await this.$refs[ref].validate();
      }

      if(isRatingInputValid) {
        // Clear unnecessary data
        if(this.isMethodGranularPostcode) {
          // Postcode Distribution method - Clear the Postcode Distribution table
          this.appData.transaction.K_PostcodeDistributionJson.splice(0, this.appData.transaction.K_PostcodeDistributionJson.length);
          delete this.appData.transaction.pdTotal;
          delete this.appData.transaction.pdVehNumTotal;
        }
        else {
          // Granular Postcode Information method - Clear the state and postcode from vehicles table
          this.appData.transaction.K_VehiclesSummary.forEach(veh => {
            this.$set(veh, 'K_State', null);
            this.$set(veh, 'K_Postcode', null);
          });
        }

        this.$emit('calculatePremiums');
      }
      else {
        this.$emit('handleError', 'Please complete all mandatory fields required for rating.');
      }
    },

    savePremiumPage: function() {
      this.$emit('savePremiumPage');
    },

    validateAdditional: async function () {
      const quoteRefs = ['quote0Component', 'quote1Component', 'quote2Component'];
      for (const refName of quoteRefs) {
        const quoteComponent = this.$refs[refName];
        if (quoteComponent && typeof quoteComponent.validateForm === 'function') {
          const isValid = await quoteComponent.validateForm();
          if (!isValid) {
            return false;
          }
        }
      }
      return true;
    },

    confirmFleetDataLoad: function () {
      Swal.fire({
        titleText: "Warning",
        html: `<p class="text-justify">This action will <b>clear all vehicle/postcode summary data</b>, and reload the data from the fleet declaration.
               You will also need to recalculate the premiums.<br/><br/>Are you sure you want to continue?</p>`,
        icon: "warning",
        allowOutsideClick: false,
        allowEnterKey: false,
        confirmButtonText: 'Yes',
        showConfirmButton: true,
        showCancelButton: true,
        showCloseButton: false
      })
        .then(result => {
          if(result && result.isConfirmed) {
            this.$emit('getVehicleSummaryFromFleetDeclaration');
          }
        });

    },

    refreshUwApprovals: function () {
      this.$emit('refreshUwApprovals');
    },

    confirmApprove: function (approval) {
      this.rejectionApprovalModalError = null;

      const tempApproval = _.cloneDeep(approval);
      tempApproval.selectedFiles = [];
      this.approvalBeingActioned = tempApproval;
      this.$refs["approveModal"].show();
    },

    confirmReject: function (approval) {
      this.rejectionApprovalModalError = null;

      const tempApproval = _.cloneDeep(approval);
      tempApproval.selectedFiles = [];
      this.approvalBeingActioned = tempApproval;
      this.$refs["rejectModal"].show();
    },

    approveApproval: function () {
      this.$emit('approveApproval', this.approvalBeingActioned);
    },

    rejectApproval: async function (bvModalEvent) {
      if(!this.approvalBeingActioned.K_Comments) {
        this.rejectionApprovalModalError = 'Comments are mandatory.';
        bvModalEvent.preventDefault();
        return;
      }
      this.$emit('rejectApproval', this.approvalBeingActioned);
    },

    getApprovalStatusVariant: function(approvalStatus) {
      let variant = 'info';
      switch (approvalStatus) {
        case 'Awaiting Approval': variant = 'warning'; break;
        case 'Approved': variant = 'success'; break;
        case 'Rejected': variant = 'danger'; break;
      }
      return variant;
    },

    isUserEligibleForApproval: function (approval) {
      const userAuthLevel = this.appData.user ? this.appData.user.authLevel : 0;

      let approvalLevel = 0;
      if(approval.K_Level) {
        if(approval.K_Level.startsWith('Level-1')) {
          approvalLevel = 1;
        }
        else if(approval.K_Level.startsWith('Level-2')) {
          approvalLevel = 2;
        }
        else if(approval.K_Level.startsWith('Level-3')) {
          approvalLevel = 3;
        }
        else if(approval.K_Level.startsWith('Level-4')) {
          approvalLevel = 3;
        }
      }
      return userAuthLevel >= approvalLevel;
    },

    excessSelected: function () {
      if(!this.appData.transaction.K_RequiredExcess) {
        this.appData.transaction.K_AlternateExcess1 = null;
        this.appData.transaction.K_AlternateExcess2 = null;
      }

      if(this.appData.transaction.K_RequiredExcess && !this.appData.transaction.K_AlternateExcess1) {
        this.appData.transaction.K_AlternateExcess2 = null;
      }
    },

    getQuoteTitle: function (quote) {
      const numericalExcess = Number(quote.K_Excess);
      const formatter = new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: 'AUD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      });
      return `${formatter.format(numericalExcess)} Excess`;
    },

    getQuoteExcess: function (quoteId) {
      const quotes = this.appData?.quotes;
      if(Array.isArray(quotes) && quotes.length > 0) {
        const quote = _.find(this.appData?.quotes, { 'id': quoteId });
        if(quote) {
          const numericalExcess = Number(quote.K_Excess);
          const formatter = new Intl.NumberFormat('en-AU', {
            style: 'currency',
            currency: 'AUD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
          });
          return `${formatter.format(numericalExcess)} Excess`;
        }
      }
    }
  }
};
</script>
